<template>

<com-item name="game.momentum" :size="size" :loading="loading" :is-empty="!active" :any-height="true" title="Momentum">

	<template v-slot:about>

		<p>How {{ your }} play relationship with this game has changed over time.</p>

	</template>

	<template v-slot:stats>

		<div class="momentum" v-if="active">

			<div class="momentum-verdict" :class="'is-' + verdict.theme">

				<div class="momentum-verdict-main">

					<i class="momentum-verdict-icon" :class="verdict.icon" />

					<div class="momentum-verdict-label">{{ verdict.label }}</div>

				</div>

				<div class="momentum-verdict-stat" v-if="changeStat">{{ changeStat }}</div>

			</div>

			<div class="momentum-chart" v-if="years.length">

				<div class="momentum-chart-bars">

					<div class="momentum-chart-bar" v-for="(year, index) in years" :key="index" v-tooltip="year.tooltip">

						<div class="momentum-chart-bar-fill" :class="{ 'is-peak': year.isPeak, 'is-current': year.isCurrent }" :style="{ height: year.height + '%' }"></div>

					</div>

				</div>

				<div class="momentum-chart-labels">

					<div class="momentum-chart-label" v-for="(year, index) in years" :key="'l' + index" :class="{ 'is-peak': year.isPeak, 'is-current': year.isCurrent }">{{ year.short }}</div>

				</div>

			</div>

			<div class="momentum-peak" v-if="peak">

				<div class="momentum-peak-marker" />

				<div class="momentum-peak-text">Peak: <b>{{ peak.year }}</b> · {{ peak.plays }} play{{ peak.plays === 1 ? '' : 's' }}</div>

			</div>

			<div class="momentum-compare">

				<div class="momentum-compare-item" v-if="hasVelocity">

					<div class="momentum-compare-head">

						<div class="momentum-compare-title">Velocity</div>

						<div class="momentum-compare-badge" :class="velocityDirection">{{ velocityLabel }}</div>

					</div>

					<div class="momentum-compare-bars">

						<div class="momentum-compare-bar">
							<div class="momentum-compare-bar-label">First half</div>
							<div class="momentum-compare-bar-track"><div class="momentum-compare-bar-fill" :style="{ width: velocityFirstPct + '%' }" /></div>
							<div class="momentum-compare-bar-value">{{ data.velocity.firstHalf }}</div>
						</div>

						<div class="momentum-compare-bar">
							<div class="momentum-compare-bar-label">Second half</div>
							<div class="momentum-compare-bar-track"><div class="momentum-compare-bar-fill" :style="{ width: velocitySecondPct + '%' }" /></div>
							<div class="momentum-compare-bar-value">{{ data.velocity.secondHalf }}</div>
						</div>

					</div>

				</div>

				<div class="momentum-compare-item" v-if="hasHoneymoon">

					<div class="momentum-compare-head">

						<div class="momentum-compare-title">Honeymoon</div>

						<div class="momentum-compare-badge" :class="honeymoonDirection">{{ honeymoonLabel }}</div>

					</div>

					<div class="momentum-compare-bars">

						<div class="momentum-compare-bar">
							<div class="momentum-compare-bar-label">{{ honeymoonFirstLabel }}</div>
							<div class="momentum-compare-bar-track"><div class="momentum-compare-bar-fill" :style="{ width: honeymoonFirstPct + '%' }" /></div>
							<div class="momentum-compare-bar-value">{{ data.honeymoon.firstRate }}<small>/mo</small></div>
						</div>

						<div class="momentum-compare-bar" v-if="data.honeymoon.restMonths > 0">
							<div class="momentum-compare-bar-label">After</div>
							<div class="momentum-compare-bar-track"><div class="momentum-compare-bar-fill" :style="{ width: honeymoonRestPct + '%' }" /></div>
							<div class="momentum-compare-bar-value">{{ data.honeymoon.restRate }}<small>/mo</small></div>
						</div>

					</div>

				</div>

			</div>

		</div>

	</template>

</com-item>

</template>

<script>

import comItem from './../common/Item'

export default {

	props: ['fetching', 'your', 'you', 'size'],

	components: {
		'com-item': comItem
	},

	computed: {

		loading: function() {

			return this.fetching === 'all'

		},

		data: function() {

			return this.$store.getters['page/insights/game/plays']

		},

		active: function() {

			return this.data && !this.loading && this.data.byYear && this.data.byYear.length > 0

		},

		hasVelocity: function() {

			return this.data && this.data.velocity && (this.data.velocity.firstHalf > 0 || this.data.velocity.secondHalf > 0)

		},

		hasHoneymoon: function() {

			return this.data && this.data.honeymoon && (this.data.honeymoon.firstRate > 0 || this.data.honeymoon.restRate > 0)

		},

		years: function() {

			if (!this.data || !this.data.byYear || !this.data.byYear.length) return []

			var currentYear = new Date().getFullYear()
			var currentYearStr = currentYear.toString()

			// Build a map from the data
			var yearMap = {}

			this.data.byYear.forEach(function(y) {

				yearMap[y.year] = y.plays

			})

			// Find the range: first year with data to current year
			var firstYear = parseInt(this.data.byYear[0].year)
			var lastYear = currentYear

			// Build full range including empty years
			var full = []
			var max = 0
			var peakIndex = 0

			for (var y = firstYear; y <= lastYear; y++) {

				var plays = yearMap[y.toString()] || 0
				full.push({ year: y.toString(), plays: plays })

				if (plays > max) {
					max = plays
					peakIndex = full.length - 1
				}

			}

			if (!max) return []

			return full.map(function(y, i) {

				return {
					year: y.year,
					short: y.year,
					plays: y.plays,
					height: y.plays > 0 ? Math.max(4, Math.round((y.plays / max) * 100)) : 0,
					isPeak: i === peakIndex,
					isCurrent: y.year === currentYearStr,
					tooltip: y.year + '<small>' + y.plays + ' play' + (y.plays === 1 ? '' : 's') + '</small>'
				}

			})

		},

		peak: function() {

			if (!this.years.length) return null

			for (var i = 0; i < this.years.length; i++) {

				if (this.years[i].isPeak) return this.years[i]

			}

			return null

		},

		rolling12: function() {

			if (!this.data || !this.data.byMonth || !this.data.byMonth.length) return 0

			var now = new Date()
			var cutoff = new Date(now.getFullYear(), now.getMonth() - 12, 1)
			var cutoffKey = cutoff.getFullYear() + '-' + String(cutoff.getMonth() + 1).padStart(2, '0')
			var total = 0

			this.data.byMonth.forEach(function(m) {
				if (m.label >= cutoffKey) total += m.plays
			})

			return total

		},

		changeStat: function() {

			if (!this.peak || !this.years.length) return null
			if (this.peak.plays === 0) return null

			var recent = this.rolling12

			if (recent === this.peak.plays) return null

			var pct = Math.round(((this.peak.plays - recent) / this.peak.plays) * 100)

			if (pct > 0) return 'Down ' + pct + '% from ' + this.peak.year + ' peak'
			if (pct < 0) return 'Up ' + Math.abs(pct) + '% vs ' + this.peak.year

			return null

		},

		velocityFirstPct: function() {

			if (!this.hasVelocity) return 0
			var max = Math.max(this.data.velocity.firstHalf, this.data.velocity.secondHalf)
			return max > 0 ? Math.round(this.data.velocity.firstHalf / max * 100) : 0

		},

		velocitySecondPct: function() {

			if (!this.hasVelocity) return 0
			var max = Math.max(this.data.velocity.firstHalf, this.data.velocity.secondHalf)
			return max > 0 ? Math.round(this.data.velocity.secondHalf / max * 100) : 0

		},

		velocityDirection: function() {

			if (!this.hasVelocity) return ''
			if (this.data.velocity.secondHalf > this.data.velocity.firstHalf) return 'is-up'
			if (this.data.velocity.secondHalf < this.data.velocity.firstHalf) return 'is-down'
			return 'is-flat'

		},

		velocityLabel: function() {

			if (!this.hasVelocity || !this.data.velocity.firstHalf) return ''
			var ratio = this.data.velocity.secondHalf / this.data.velocity.firstHalf
			if (ratio > 1) return '+' + Math.round((ratio - 1) * 100) + '%'
			if (ratio < 1) return '-' + Math.round((1 - ratio) * 100) + '%'
			return 'Even'

		},

		honeymoonFirstLabel: function() {

			if (!this.data || !this.data.honeymoon) return 'First 3 months'

			var months = this.data.honeymoon.firstMonths || 3

			if (this.data.honeymoon.restMonths > 0) return 'First ' + months + ' month' + (months === 1 ? '' : 's')

			return months + ' month' + (months === 1 ? '' : 's') + ' total'

		},

		honeymoonFirstPct: function() {

			if (!this.hasHoneymoon) return 0
			var max = Math.max(this.data.honeymoon.firstRate, this.data.honeymoon.restRate)
			return max > 0 ? Math.round(this.data.honeymoon.firstRate / max * 100) : 0

		},

		honeymoonRestPct: function() {

			if (!this.hasHoneymoon) return 0
			var max = Math.max(this.data.honeymoon.firstRate, this.data.honeymoon.restRate)
			return max > 0 ? Math.round(this.data.honeymoon.restRate / max * 100) : 0

		},

		honeymoonDirection: function() {

			if (!this.hasHoneymoon) return ''
			if (!this.data.honeymoon.restMonths) return 'is-flat'
			if (this.data.honeymoon.restRate > this.data.honeymoon.firstRate) return 'is-up'
			if (this.data.honeymoon.restRate < this.data.honeymoon.firstRate) return 'is-down'
			return 'is-flat'

		},

		honeymoonLabel: function() {

			if (!this.hasHoneymoon || !this.data.honeymoon.firstRate) return ''
			if (!this.data.honeymoon.restMonths) return 'Early days'
			var ratio = this.data.honeymoon.restRate / this.data.honeymoon.firstRate
			if (ratio > 1) return '+' + Math.round((ratio - 1) * 100) + '%'
			if (ratio < 1) return '-' + Math.round((1 - ratio) * 100) + '%'
			return 'Even'

		},

		recentRatio: function() {

			if (!this.years || this.years.length < 2) return 1

			var currentYear = new Date().getFullYear().toString()
			var totalPlays = 0
			var completeYears = 0

			for (var i = 0; i < this.years.length; i++) {

				if (this.years[i].year !== currentYear) {

					totalPlays += this.years[i].plays
					completeYears++

				}

			}

			if (completeYears === 0 || totalPlays === 0) return 1

			var avgYearly = totalPlays / completeYears

			return this.rolling12 / avgYearly

		},

		verdict: function() {

			var velocityRatio = 1
			var honeymoonRatio = 1

			if (this.hasVelocity && this.data.velocity.firstHalf > 0) {

				velocityRatio = this.data.velocity.secondHalf / this.data.velocity.firstHalf

			}

			if (this.hasHoneymoon && this.data.honeymoon.firstRate > 0) {

				honeymoonRatio = this.data.honeymoon.restRate / this.data.honeymoon.firstRate

			}

			// Blend structural trend (velocity + honeymoon) with recent activity
			// Recent activity weighted more heavily — it's what the user actually feels
			var structural = (velocityRatio + honeymoonRatio) / 2
			var score = (structural * 0.35) + (this.recentRatio * 0.65)

			if (score >= 2.0) return { label: 'Surging', theme: 'surge', icon: 'fa fa-rocket' }
			if (score >= 1.3) return { label: 'Heating up', theme: 'hot', icon: 'fa fa-fire' }
			if (score >= 0.85) return { label: 'Steady', theme: 'steady', icon: 'fa fa-equals' }
			if (score >= 0.5) return { label: 'Cooling off', theme: 'cool', icon: 'fa fa-temperature-low' }
			if (score >= 0.15) return { label: 'Fading', theme: 'fading', icon: 'fa fa-cloud' }
			if (this.rolling12 > 0) return { label: 'Dormant', theme: 'dormant', icon: 'fa fa-snowflake' }
			return { label: 'Inactive', theme: 'inactive', icon: 'fa fa-moon' }

		}

	}

}

</script>

<style scoped>

.momentum {
	width: 100%;
}

/* Verdict */

.momentum-verdict {
	padding: 12px 15px;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.momentum-verdict-main {
	display: flex;
	align-items: center;
	gap: 8px;
}

.momentum-verdict-icon {
	font-size: 18px;
}

.momentum-verdict-label {
	font-size: 18px;
	font-weight: 700;
	color: #333;
}

.momentum-verdict-stat {
	font-size: 12px;
	font-weight: 500;
	color: #333;
	text-align: right;
}

.momentum-verdict.is-surge .momentum-verdict-icon {
	color: #e74c3c;
}

.momentum-verdict.is-hot .momentum-verdict-icon {
	color: #c0392b;
}

.momentum-verdict.is-steady .momentum-verdict-icon {
	color: #2FC482;
}

.momentum-verdict.is-cool .momentum-verdict-icon {
	color: #4881bb;
}

.momentum-verdict.is-fading .momentum-verdict-icon {
	color: #7f8c8d;
}

.momentum-verdict.is-dormant .momentum-verdict-icon {
	color: #999;
}

.momentum-verdict.is-inactive .momentum-verdict-icon {
	color: #bbb;
}

/* Year chart */

.momentum-chart {
	padding: 8px 15px 0;
}

.momentum-chart-bars {
	display: flex;
	align-items: flex-end;
	height: 80px;
	gap: 2px;
}

.momentum-chart-bar {
	flex: 1;
	height: 100%;
	display: flex;
	align-items: flex-end;
	cursor: pointer;
}

.momentum-chart-bar:hover .momentum-chart-bar-fill {
	opacity: 0.7;
}

.momentum-chart-bar-fill {
	width: 100%;
	min-height: 2px;
	border-radius: 2px 2px 0 0;
	background-color: rgba(72, 129, 187, 0.3);
	transition: height 0.4s ease;
}

.momentum-chart-bar-fill.is-peak {
	background-color: #4881bb;
}

.momentum-chart-bar-fill.is-current {
	background-color: rgba(72, 129, 187, 0.6);
}

.momentum-chart-labels {
	display: flex;
	gap: 2px;
	margin-top: 6px;
}

.momentum-chart-label {
	flex: 1;
	text-align: center;
	font-size: 12px;
	color: #999;
	font-weight: 400;
}

.momentum-chart-label.is-peak {
	color: #4881bb;
	font-weight: 700;
}

.momentum-chart-label.is-current {
	color: #333;
	font-weight: 600;
}

/* Peak callout */

.momentum-peak {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 15px;
}

.momentum-peak-marker {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #4881bb;
	flex-shrink: 0;
}

.momentum-peak-text {
	font-size: 12px;
	color: #666;
}

.momentum-peak-text b {
	color: #333;
}

/* Compare sections */

.momentum-compare {
	display: flex;
	flex-direction: column;
}

.momentum-compare-item {
	padding: 10px 15px;
}

.momentum-compare-item:nth-child(odd) {
	background-color: rgba(12, 137, 178, 0.05);
}

.momentum-compare-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 6px;
}

.momentum-compare-title {
	font-size: 12px;
	font-weight: 600;
	color: #333;
}

.momentum-compare-badge {
	font-size: 12px;
	font-weight: 700;
	padding: 2px 8px;
	border-radius: 10px;
}

.momentum-compare-badge.is-up {
	background-color: rgba(47, 196, 130, 0.12);
	color: #1a9960;
}

.momentum-compare-badge.is-down {
	background-color: rgba(223, 71, 81, 0.12);
	color: #c0392b;
}

.momentum-compare-badge.is-flat {
	background-color: rgba(0, 0, 0, 0.06);
	color: #888;
}

.momentum-compare-bars {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.momentum-compare-bar {
	display: flex;
	align-items: center;
	gap: 6px;
}

.momentum-compare-bar-label {
	width: 90px;
	flex-shrink: 0;
	font-size: 12px;
	color: #666;
	white-space: nowrap;
}

.momentum-compare-bar-track {
	flex: 1;
	height: 5px;
	background-color: rgba(0, 0, 0, 0.04);
	border-radius: 3px;
	overflow: hidden;
}

.momentum-compare-bar-fill {
	height: 100%;
	background-color: #4881bb;
	border-radius: 3px;
}

.momentum-compare-bar-value {
	width: 44px;
	flex-shrink: 0;
	text-align: right;
	font-size: 13px;
	font-weight: 700;
	color: #333;
}

.momentum-compare-bar-value small {
	font-size: 12px;
	font-weight: 400;
	color: #999;
}

</style>
